import React, {useRef, useState} from 'react';
import QuestionLayout from './QuestionLayout';
import {resolutionStaffConfig,resolutionStaffValue,resolutionFromStaffValue} from '../utils/dominantResolution.js';
import {RomanNumeralInput} from './RomanNumeralAnalysis';
import {renderClef} from './staff/staffRender';
import {pitchToY, snapYToPitch} from './staff/pitchMath';
import {Glyph} from './staff/glyphs';

const BRACE_PATH = "M-12 -656c0 -352 -132 -696 -132 -1032c0 -180 36 -344 156 -484c4 -4 8 -8 8 -12c0 -8 -12 -16 -20 -16c-4 0 -8 0 -12 4c-160 184 -216 416 -216 656c0 360 140 712 140 1052c0 176 -40 344 -160 480v8v8c120 136 160 304 160 480c0 340 -140 692 -140 1052 c0 240 56 472 216 656c4 4 8 4 12 4c8 0 20 -8 20 -16c0 -4 -4 -8 -8 -12c-120 -140 -156 -304 -156 -484c0 -336 132 -680 132 -1032c0 -240 -52 -472 -208 -656c156 -184 208 -416 208 -656z";

const TOP={treble:55,bass:155};
const WIDTH=360, CENTERS=[180,290];
const SIGNATURE_POSITIONS={
  treble:{sharps:['F5','C5','G5','D5','A4','E5','B4'],flats:['B4','E5','A4','D5','G4','C5','F4']},
  bass:{sharps:['F3','C3','G3','D3','A2','E3','B2'],flats:['B2','E3','A2','D3','G2','C3','F2']}
};
function signatureEntries(signature,staff) {
  if(Array.isArray(signature?.[1])) return signature[1];
  return (SIGNATURE_POSITIONS[staff][signature?.type] || []).slice(0,signature?.count || 0)
    .map(pitch=>({pitch,accidental:signature.type==='flats'?'b':'#'}));
}
function noteAccidental(pitch,signature,staff) {
  const explicit=String(pitch).match(/^[A-G]([#bn♮]+)/)?.[1];
  if(!explicit) return undefined;
  const implied=signatureEntries(signature,staff).find(n=>n.pitch[0]===pitch[0])?.accidental;
  return explicit===implied ? undefined : explicit==='n'?'♮':explicit;
}
function Note({pitch,staff,x,signature,fill='#111'}) {
  const y=pitchToY(pitch,staff,{staffTop:TOP[staff]});
  if(!Number.isFinite(y)) return null;
  const ledgers=[];
  for(let line=TOP[staff]-10;line>=y;line-=10) ledgers.push(line);
  for(let line=TOP[staff]+50;line<=y;line+=10) ledgers.push(line);
  return <g pointerEvents="none">
    {ledgers.map(line=><line key={line} x1={x-8} x2={x+23} y1={line} y2={line} stroke={fill} />)}
    <g transform={`translate(${x},${y})`}><Glyph symbol="whole_note" fill={fill} accidental={noteAccidental(pitch,signature,staff)} /></g>
  </g>;
}
function CadenceScore({example,index,signature={},answer,change,mode,feedback,keyLabel}) {
  const [hover,setHover]=useState(null);
  const editable=mode==='edit';
  function position(event,staff) {
    const svg=event.currentTarget.ownerSVGElement,point=svg.createSVGPoint();
    point.x=event.clientX;point.y=event.clientY;
    const local=point.matrixTransform(svg.getScreenCTM().inverse());
    return snapYToPitch(local.y,staff,{staffTop:TOP[staff],allowLedgerLines:true});
  }
  function toggle(staff,pitch) {
    if(!editable) return;
    const chord=answer[2] || {}, notes=chord[staff] || [];
    const exists=notes.some(n=>n.pitch===pitch);
    if(!exists && notes.length >= (staff==='treble'?3:1)) return;
    change({...answer,2:{...chord,[staff]:exists?notes.filter(n=>n.pitch!==pitch):[...notes,{pitch,symbol:'whole_note'}]}});
  }
  return <section data-cadence-example={index+1} style={{width:360,flex:'0 0 360px',minWidth:0}}>
    <h4 style={{margin:'0 0 8px',height:45,lineHeight:'1.4',fontSize:16,textAlign:'center'}}>{example.label}</h4>
    <svg viewBox={`0 0 ${WIDTH} 280`} width="100%" style={{display:'block',background:'#fff',borderRadius:4}}
      aria-label={`Example ${index+1}: given first chord and second chord to complete`}>
      <g data-grand-staff-brace transform="translate(44,125) scale(0.0318181818)"><path d={BRACE_PATH} fill="#111" /></g>
      {[45,235].map(x=><line key={x} x1={x} x2={x} y1={55} y2={195} stroke="#111" />)}
      <g data-cadence-end-barline>
        {[344,350].map(x=><line key={x} x1={x} x2={x} y1={55} y2={195} stroke="#111" strokeWidth={1.5} />)}
      </g>
      {['treble','bass'].map(staff=><g key={staff}>
        {Array.from({length:5},(_,n)=><line key={n} x1={45} x2={350} y1={TOP[staff]+n*10} y2={TOP[staff]+n*10} stroke="#111" />)}
        {renderClef(staff,TOP[staff],-6)}
        {signatureEntries(signature[staff],staff).map((note,n)=><g key={n} transform={`translate(${95+n*9},${pitchToY(note.pitch,staff,{staffTop:TOP[staff]})})`}>
          <Glyph symbol="whole_note" accidental={note.accidental} accidentalOnly />
        </g>)}
        {(example.given?.[staff]?.notes || []).map((pitch,n)=><Note key={n} pitch={pitch} staff={staff} x={CENTERS[0]} signature={signature[staff]} />)}
        {(answer[2]?.[staff] || []).map((note,n)=><Note key={n} pitch={note.pitch} staff={staff} x={CENTERS[1]} signature={signature[staff]} />)}
        {editable && hover?.staff===staff && !(answer[2]?.[staff] || []).some(n=>n.pitch===hover.pitch) &&
          (answer[2]?.[staff] || []).length<(staff==='treble'?3:1) &&
          <Note pitch={hover.pitch} staff={staff} x={CENTERS[1]} signature={signature[staff]} fill="#94a3b8" />}
        <rect data-input-staff={staff} x={236} y={staff==='treble'?10:130} width={113} height={staff==='treble'?115:100}
          fill="transparent" style={{cursor:editable?'crosshair':'default',touchAction:'manipulation'}}
          onPointerMove={event=>editable && setHover({staff,pitch:position(event,staff)})}
          onPointerLeave={()=>setHover(null)} onClick={event=>toggle(staff,position(event,staff))} />
      </g>)}
      {keyLabel && <foreignObject x={0} y={235} width={130} height={35}>
        <div data-cadence-key-label style={{height:35,display:'flex',alignItems:'center',justifyContent:'flex-end',fontWeight:700,whiteSpace:'nowrap',color:'#111'}}>{keyLabel}</div>
      </foreignObject>}
      {[1,2].map(slot=>{
        const result=feedback?.parts?.find(p=>p.id===`rn-${index}-${slot}`);
        return <foreignObject key={slot} x={CENTERS[slot-1]-34} y={235} width={82} height={40}>
          <RomanNumeralInput aria-label={`Example ${index+1}, chord ${slot} Roman numeral`} className="rn-font"
            style={{boxSizing:'border-box',width:78,height:35,textAlign:'center',background:'#fff',color:'#111',fontFamily:'TheoryRomanNumerals',fontSize:24,
              border:`1px solid ${mode==='review' && result?(result.correct?'#2e7d32':'#c62828'):'#94a3b8'}`,borderRadius:4}}
            value={answer.typed?.[slot] || ''} disabled={!editable}
            onChange={text=>change({...answer,typed:{...answer.typed,[slot]:text}})} />
        </foreignObject>;
      })}
    </svg>
  </section>;
}
export default function CadenceCompletion({config,value={},onChange,mode='edit',feedback,Staff}) {
  const scrollRef=useRef(null);
  if (config.dominantResolution) return <Staff config={resolutionStaffConfig(config)} value={resolutionStaffValue(value,config)} onChange={next=>onChange(resolutionFromStaffValue(next,value,config))} mode={mode} feedback={feedback} />;
  const signature=config.keySignature?.treble;
  const entries=signatureEntries(signature,'treble');
  const flat=signature?.type==='flats' || entries[0]?.accidental==='b';
  const key=(flat?['C','F','B♭','E♭','A♭','D♭','G♭','C♭']:['C','G','D','A','E','B','F♯','C♯'])[entries.length] || 'C';
  return <QuestionLayout horizontalScrollRef={scrollRef} number={config.number} prompt={config.prompt} points={config.points} mode={mode} feedback={feedback}>
    <div style={{width:'100%',minWidth:0}}>
      {mode==='edit' && <p style={{margin:'0 0 16px',fontSize:14}}>Click in each second measure to add whole notes. Click a note again to remove it. Enter Roman numerals beneath both chords.</p>}
      <div ref={scrollRef} data-cadence-row style={{display:'flex',flexWrap:'nowrap',gap:16,alignItems:'start',width:'100%',maxWidth:'100%',overflowX:'auto',paddingBottom:12}}>
        {(config.cadenceExamples || []).map((example,index)=><CadenceScore key={index} example={example} index={index}
          keyLabel={index===0?(config.keyLabel || `${key} Major`):null} signature={config.keySignature} answer={value.examples?.[index] || {}} mode={mode} feedback={feedback}
          change={answer=>onChange({...value,examples:{...value.examples,[index]:answer}})} />)}
      </div>
    </div>
  </QuestionLayout>;
}
